import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import Notification from './Notification';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0b1120 0%, #1a1a2e 100%);
  color: white;
  padding: 6rem 1.5rem 3rem;
`;

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid rgba(148, 163, 184, 0.6);
  color: #e5e7eb;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  cursor: pointer;
  margin-bottom: 1.5rem;

  &:hover {
    background: rgba(148, 163, 184, 0.15);
  }
`;

const OrderHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #3b82f6, #9333ea);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.1rem;
  }
`;

const OrderLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 3rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const OrderForm = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(0.5rem);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.5rem;
  padding: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: white;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-weight: 500;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  color: white;
  font-size: 0.95rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Select = styled.select`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  color: white;
  font-size: 0.95rem;
  
  option {
    background: #1a1a2e;
    color: white;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const OrderSummary = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(0.5rem);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.5rem;
  padding: 2rem;
  height: fit-content;
`;

const ProductCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const ProductHeader = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  
  .product-image {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #3b82f6, #9333ea);
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
  }
  
  .product-info {
    flex: 1;
    
    h3 {
      font-size: 1.1rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
    }
    
    p {
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.9rem;
    }
  }
`;

const PriceBreakdown = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1rem;
  
  .price-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    font-size: 0.95rem;
    
    &.total {
      font-weight: 700;
      font-size: 1.2rem;
      color: #10b981;
      margin-top: 0.5rem;
      padding-top: 0.5rem;
      border-top: 1px solid rgba(255, 255, 255, 0.2);
    }
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 1rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(16, 185, 129, 0.3);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const DeliveryOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const DeliveryOption = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid ${props => props.selected ? '#3b82f6' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 0.75rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.15);
  }
  
  .option-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    
    h4 {
      font-weight: 600;
      color: white;
    }
    
    .price {
      color: #10b981;
      font-weight: 600;
    }
  }
  
  .option-details {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
  }
`;

function OrderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('product');
  const offerId = searchParams.get('offer');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Sénégal',
    storage: '128GB',
    color: 'Noir',
    delivery: 'express'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState(null);

  // Données des produits
  const products = {
    'iphone-15': {
      id: 'iphone-15',
      name: 'iPhone 15',
      price: 589000,
      emoji: '📱'
    },
    'iphone-15-pro': {
      id: 'iphone-15-pro',
      name: 'iPhone 15 Pro',
      price: 799000,
      emoji: '📱'
    },
    'iphone-15-pro-max': {
      id: 'iphone-15-pro-max',
      name: 'iPhone 15 Pro Max',
      price: 918000,
      emoji: '📱'
    }
  };

  // Données des offres flash
  const flashOffers = {
    'flash-iphone15': {
      title: 'Flash Deal iPhone 15',
      discountedPrice: 500650,
      originalPrice: 589000,
      discount: 88350
    },
    'flash-pro-max': {
      title: 'Flash Deal iPhone 15 Pro Max',
      discountedPrice: 734400,
      originalPrice: 918000,
      discount: 183600
    }
  };

  const product = products[productId];
  const flashOffer = flashOffers[offerId];
  const finalPrice = flashOffer ? flashOffer.discountedPrice : product?.price || 0;

  // Debug pour voir les paramètres reçus
  console.log('OrderPage - productId:', productId);
  console.log('OrderPage - offerId:', offerId);
  console.log('OrderPage - product:', product);
  console.log('OrderPage - available products:', Object.keys(products));

  const deliveryOptions = [
    {
      id: 'express',
      name: 'Livraison Express',
      price: 0,
      time: 'Demain avant 13h',
      details: 'Suivi en temps réel, signature requise'
    },
    {
      id: 'standard',
      name: 'Livraison Standard',
      price: 5000,
      time: '3-5 jours ouvrables',
      details: 'Suivi standard, point relais possible'
    },
    {
      id: 'pickup',
      name: 'Retrait en magasin',
      price: 0,
      time: 'Disponible immédiatement',
      details: 'Dakar Plateau, horaires 9h-18h'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simuler la soumission de la commande
    setTimeout(() => {
      const deliveryOption = deliveryOptions.find(d => d.id === formData.delivery);
      const orderData = {
        id: Date.now(),
        product: productId,
        offer: offerId,
        customer: formData,
        price: finalPrice,
        delivery: formData.delivery,
        timestamp: new Date().toISOString(),
        productName: product.name,
        storage: formData.storage,
        color: formData.color,
        deliveryOption: deliveryOption.name,
        totalPrice: finalPrice
      };
      
      console.log('Commande soumise:', orderData);
      
      // Afficher la notification au lieu de l'alert
      setNotificationData(orderData);
      setShowNotification(true);
      
      setIsSubmitting(false);
    }, 2000);
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
    navigate('/');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!product) {
    console.error('Produit non trouvé - productId:', productId);
    console.error('Produits disponibles:', Object.keys(products));
    
    // Redirection automatique après 2 secondes
    setTimeout(() => {
      navigate('/');
    }, 2000);
    
    return (
      <PageWrapper>
        <Container>
          <BackButton onClick={() => navigate(-1)}>← Retour</BackButton>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>❌ Produit non trouvé</h2>
            <p>Le produit demandé n'est pas disponible.</p>
            <p>Redirection vers l'accueil dans 2 secondes...</p>
            <BackButton onClick={() => navigate('/')} style={{ marginTop: '1rem' }}>
              Retour à l'accueil
            </BackButton>
          </div>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <BackButton onClick={() => navigate(-1)}>← Retour</BackButton>
        
        <OrderHeader>
          <h1>Finaliser votre commande</h1>
          <p>
            {flashOffer ? 'Profitez de votre offre flash exclusive!' : 'Configurez votre iPhone et choisissez la livraison'}
          </p>
        </OrderHeader>

        <OrderLayout>
          <OrderForm>
            <SectionTitle>📍 Informations de livraison</SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="firstName">Prénom</Label>
                <Input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="lastName">Nom</Label>
                <Input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup className="full-width">
                <Label htmlFor="address">Adresse</Label>
                <Input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="city">Ville</Label>
                <Input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="postalCode">Code postal</Label>
                <Input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </FormGrid>

            <SectionTitle>⚙️ Configuration du produit</SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="storage">Stockage</Label>
                <Select
                  id="storage"
                  name="storage"
                  value={formData.storage}
                  onChange={handleChange}
                >
                  <option value="128GB">128 GB</option>
                  <option value="256GB">256 GB</option>
                  <option value="512GB">512 GB</option>
                  <option value="1TB">1 TB</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="color">Couleur</Label>
                <Select
                  id="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                >
                  <option value="Noir">Noir</option>
                  <option value="Bleu">Bleu</option>
                  <option value="Blanc">Blanc</option>
                  <option value="Rose">Rose</option>
                  <option value="Titane">Titane</option>
                </Select>
              </FormGroup>
            </FormGrid>

            <SectionTitle>🚚 Options de livraison</SectionTitle>
            
            <DeliveryOptions>
              {deliveryOptions.map(option => (
                <DeliveryOption
                  key={option.id}
                  selected={formData.delivery === option.id}
                  onClick={() => setFormData({...formData, delivery: option.id})}
                >
                  <div className="option-header">
                    <h4>{option.name}</h4>
                    <span className="price">
                      {option.price === 0 ? 'GRATUIT' : `${option.price.toLocaleString()} FCFA`}
                    </span>
                  </div>
                  <div className="option-details">
                    {option.time} • {option.details}
                  </div>
                </DeliveryOption>
              ))}
            </DeliveryOptions>

            <SubmitButton 
              type="submit" 
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Traitement en cours...' : `Confirmer la commande - ${finalPrice.toLocaleString()} FCFA`}
            </SubmitButton>
          </OrderForm>

          <OrderSummary>
            <SectionTitle>📋 Résumé</SectionTitle>
            
            <ProductCard>
              <ProductHeader>
                <div className="product-image">
                  {product.emoji}
                </div>
                <div className="product-info">
                  <h3>{product.name}</h3>
                  <p>
                    {formData.storage} • {formData.color}
                    {flashOffer && ' • OFFRE FLASH'}
                  </p>
                </div>
              </ProductHeader>
            </ProductCard>

            <PriceBreakdown>
              <div className="price-row">
                <span>Prix du produit:</span>
                <span>{(flashOffer ? flashOffer.originalPrice : product.price).toLocaleString()} FCFA</span>
              </div>
              
              {flashOffer && (
                <div className="price-row">
                  <span>Réduction flash:</span>
                  <span style={{color: '#ef4444'}}>-{flashOffer.discount.toLocaleString()} FCFA</span>
                </div>
              )}
              
              <div className="price-row">
                <span>Livraison:</span>
                <span>
                  {deliveryOptions.find(d => d.id === formData.delivery)?.price === 0 
                    ? 'GRATUIT' 
                    : `${deliveryOptions.find(d => d.id === formData.delivery)?.price.toLocaleString()} FCFA`
                  }
                </span>
              </div>
              
              <div className="price-row total">
                <span>Total:</span>
                <span>{finalPrice.toLocaleString()} FCFA</span>
              </div>
            </PriceBreakdown>
          </OrderSummary>
        </OrderLayout>
      </Container>
      
      {/* Notification après commande */}
      {notificationData && (
        <Notification 
          orderData={notificationData}
          onClose={handleCloseNotification}
          isVisible={showNotification}
        />
      )}
    </PageWrapper>
  );
}

export default OrderPage;
